
import { AIModule } from '../../types';

export const GAMING_AI_MODULE_4: AIModule = {
  id: 'player-analytics-adaptive-difficulty',
  title: 'Player Analytics & Adaptive Difficulty',
  description: 'Implement AI systems that analyze player behavior and dynamically adjust game difficulty for optimal engagement.',
  duration: '3.5 hours',
  difficulty: 'medium',
  content: `
# Player Analytics & Adaptive Difficulty

Modern games use AI to understand player behavior and automatically adjust difficulty to maintain optimal engagement, ensuring players stay in the "flow state" between boredom and frustration.

## Player Behavior Analytics

### Key Metrics to Track

1. **Performance Metrics**
   - Success/failure rates
   - Completion times
   - Accuracy scores
   - Deaths/retries per level

2. **Engagement Metrics**
   - Session length
   - Return frequency
   - Feature usage
   - Progression speed

3. **Behavioral Patterns**
   - Play style preferences
   - Strategy choices
   - Learning curves
   - Skill development

### Data Collection Framework

\`\`\`python
import time
import json
import statistics
from typing import Dict, List, Optional, Tuple
from dataclasses import dataclass, asdict
from enum import Enum
from collections import defaultdict, deque

class EventType(Enum):
    LEVEL_START = "level_start"
    LEVEL_COMPLETE = "level_complete"
    LEVEL_FAIL = "level_fail"
    PLAYER_DEATH = "player_death"
    ITEM_COLLECTED = "item_collected"
    ENEMY_DEFEATED = "enemy_defeated"
    SKILL_USED = "skill_used"
    PAUSE_GAME = "pause_game"
    RESUME_GAME = "resume_game"
    QUIT_GAME = "quit_game"

class DifficultyLevel(Enum):
    VERY_EASY = 1
    EASY = 2
    NORMAL = 3
    HARD = 4
    VERY_HARD = 5

@dataclass
class GameEvent:
    event_type: EventType
    timestamp: float
    player_id: str
    level_id: str
    difficulty: DifficultyLevel
    context: Dict = None
    
    def to_dict(self) -> Dict:
        return asdict(self)

@dataclass
class PlayerSession:
    session_id: str
    player_id: str
    start_time: float
    end_time: Optional[float] = None
    events: List[GameEvent] = None
    
    def __post_init__(self):
        if self.events is None:
            self.events = []
    
    def duration(self) -> float:
        if self.end_time:
            return self.end_time - self.start_time
        return time.time() - self.start_time

@dataclass
class PlayerMetrics:
    player_id: str
    total_playtime: float = 0.0
    sessions_count: int = 0
    levels_completed: int = 0
    total_deaths: int = 0
    average_completion_time: float = 0.0
    success_rate: float = 0.0
    preferred_difficulty: DifficultyLevel = DifficultyLevel.NORMAL
    skill_progression: float = 0.0  # 0.0 to 1.0
    engagement_score: float = 0.5   # 0.0 to 1.0
    
    # Behavioral patterns
    aggressive_play_style: float = 0.5    # 0.0 = cautious, 1.0 = aggressive
    exploration_tendency: float = 0.5     # 0.0 = linear, 1.0 = explorer
    patience_level: float = 0.5           # 0.0 = impatient, 1.0 = patient
    learning_speed: float = 0.5           # 0.0 = slow learner, 1.0 = fast learner

class PlayerAnalytics:
    """Advanced player behavior analytics system"""
    
    def __init__(self):
        self.sessions: Dict[str, PlayerSession] = {}
        self.player_metrics: Dict[str, PlayerMetrics] = {}
        self.global_metrics = {
            'average_session_length': 0.0,
            'completion_rates_by_level': {},
            'difficulty_distribution': defaultdict(int),
            'churn_indicators': []
        }
        
        # Sliding window for real-time analysis
        self.recent_events: deque = deque(maxlen=1000)
    
    def track_event(self, event: GameEvent):
        """Track a game event and update analytics"""
        self.recent_events.append(event)
        
        # Ensure player metrics exist
        if event.player_id not in self.player_metrics:
            self.player_metrics[event.player_id] = PlayerMetrics(event.player_id)
        
        # Update session tracking
        session_key = f"{event.player_id}_{event.timestamp // 3600}"  # Hour-based sessions
        if session_key not in self.sessions:
            self.sessions[session_key] = PlayerSession(
                session_key, event.player_id, event.timestamp
            )
        
        self.sessions[session_key].events.append(event)
        
        # Update player metrics based on event type
        self._update_player_metrics(event)
        
        # Update global metrics
        self._update_global_metrics(event)
    
    def _update_player_metrics(self, event: GameEvent):
        """Update individual player metrics"""
        metrics = self.player_metrics[event.player_id]
        
        if event.event_type == EventType.LEVEL_COMPLETE:
            metrics.levels_completed += 1
            
            # Calculate completion time if we have a level start event
            level_start_time = self._find_last_level_start(event.player_id, event.level_id)
            if level_start_time:
                completion_time = event.timestamp - level_start_time
                if metrics.average_completion_time == 0:
                    metrics.average_completion_time = completion_time
                else:
                    # Exponential moving average
                    metrics.average_completion_time = 0.8 * metrics.average_completion_time + 0.2 * completion_time
        
        elif event.event_type == EventType.PLAYER_DEATH:
            metrics.total_deaths += 1
        
        elif event.event_type == EventType.LEVEL_FAIL:
            # Analyze failure patterns for patience and learning indicators
            failure_count = self._count_recent_failures(event.player_id, event.level_id)
            if failure_count > 3:
                metrics.patience_level = max(0.0, metrics.patience_level - 0.1)
        
        # Update success rate
        total_attempts = metrics.levels_completed + self._count_total_failures(event.player_id)
        if total_attempts > 0:
            metrics.success_rate = metrics.levels_completed / total_attempts
        
        # Update skill progression based on performance trends
        self._update_skill_progression(metrics, event)
        
        # Update play style analysis
        self._analyze_play_style(metrics, event)
    
    def _update_skill_progression(self, metrics: PlayerMetrics, event: GameEvent):
        """Analyze skill progression based on performance trends"""
        if event.event_type in [EventType.LEVEL_COMPLETE, EventType.ENEMY_DEFEATED]:
            # Look at recent performance to gauge improvement
            recent_successes = self._count_recent_successes(event.player_id, window_size=10)
            if recent_successes > 7:  # 70% success rate in recent attempts
                metrics.skill_progression = min(1.0, metrics.skill_progression + 0.05)
                metrics.learning_speed = min(1.0, metrics.learning_speed + 0.02)
            elif recent_successes < 3:  # 30% success rate
                metrics.skill_progression = max(0.0, metrics.skill_progression - 0.02)
    
    def _analyze_play_style(self, metrics: PlayerMetrics, event: GameEvent):
        """Analyze and update play style indicators"""
        if event.event_type == EventType.ENEMY_DEFEATED:
            # Analyze combat approach from context
            if event.context and 'combat_duration' in event.context:
                combat_duration = event.context['combat_duration']
                if combat_duration < 5.0:  # Quick combat indicates aggression
                    metrics.aggressive_play_style = min(1.0, metrics.aggressive_play_style + 0.1)
                else:  # Longer combat indicates caution
                    metrics.aggressive_play_style = max(0.0, metrics.aggressive_play_style - 0.05)
        
        elif event.event_type == EventType.ITEM_COLLECTED:
            # Frequent item collection indicates exploration
            metrics.exploration_tendency = min(1.0, metrics.exploration_tendency + 0.05)
    
    def _find_last_level_start(self, player_id: str, level_id: str) -> Optional[float]:
        """Find the timestamp of the last level start for completion time calculation"""
        for event in reversed(self.recent_events):
            if (event.player_id == player_id and 
                event.level_id == level_id and 
                event.event_type == EventType.LEVEL_START):
                return event.timestamp
        return None
    
    def _count_recent_failures(self, player_id: str, level_id: str, window_minutes: int = 30) -> int:
        """Count recent failures for a specific level"""
        cutoff_time = time.time() - (window_minutes * 60)
        count = 0
        for event in self.recent_events:
            if (event.player_id == player_id and 
                event.level_id == level_id and 
                event.event_type == EventType.LEVEL_FAIL and 
                event.timestamp > cutoff_time):
                count += 1
        return count
    
    def _count_recent_successes(self, player_id: str, window_size: int = 10) -> int:
        """Count successes in recent events window"""
        player_events = [e for e in list(self.recent_events)[-window_size:] 
                        if e.player_id == player_id]
        return sum(1 for e in player_events if e.event_type == EventType.LEVEL_COMPLETE)
    
    def _count_total_failures(self, player_id: str) -> int:
        """Count total failures for a player"""
        return sum(1 for event in self.recent_events 
                  if event.player_id == player_id and event.event_type == EventType.LEVEL_FAIL)
    
    def _update_global_metrics(self, event: GameEvent):
        """Update global game metrics"""
        self.global_metrics['difficulty_distribution'][event.difficulty] += 1
        
        # Track completion rates by level
        if event.level_id not in self.global_metrics['completion_rates_by_level']:
            self.global_metrics['completion_rates_by_level'][event.level_id] = {'attempts': 0, 'completions': 0}
        
        if event.event_type == EventType.LEVEL_START:
            self.global_metrics['completion_rates_by_level'][event.level_id]['attempts'] += 1
        elif event.event_type == EventType.LEVEL_COMPLETE:
            self.global_metrics['completion_rates_by_level'][event.level_id]['completions'] += 1
    
    def get_player_analysis(self, player_id: str) -> Dict:
        """Get comprehensive player analysis"""
        if player_id not in self.player_metrics:
            return {"error": "Player not found"}
        
        metrics = self.player_metrics[player_id]
        
        # Calculate engagement score based on multiple factors
        engagement_factors = [
            metrics.success_rate,
            min(1.0, metrics.total_playtime / 3600),  # Normalize to hours
            metrics.skill_progression,
            1.0 - (metrics.total_deaths / max(1, metrics.levels_completed + metrics.total_deaths))
        ]
        metrics.engagement_score = statistics.mean(engagement_factors)
        
        return {
            'player_id': player_id,
            'metrics': asdict(metrics),
            'recommended_difficulty': self._recommend_difficulty(metrics),
            'churn_risk': self._calculate_churn_risk(metrics),
            'play_style_summary': self._summarize_play_style(metrics)
        }
    
    def _recommend_difficulty(self, metrics: PlayerMetrics) -> DifficultyLevel:
        """Recommend optimal difficulty based on player metrics"""
        
        # Base recommendation on success rate and skill progression
        if metrics.success_rate > 0.85 and metrics.skill_progression > 0.7:
            # Player is doing very well, increase challenge
            return DifficultyLevel(min(5, metrics.preferred_difficulty.value + 1))
        elif metrics.success_rate < 0.4 or metrics.engagement_score < 0.3:
            # Player is struggling, decrease difficulty
            return DifficultyLevel(max(1, metrics.preferred_difficulty.value - 1))
        else:
            # Player is in good flow state
            return metrics.preferred_difficulty
    
    def _calculate_churn_risk(self, metrics: PlayerMetrics) -> float:
        """Calculate probability of player churning (0.0 to 1.0)"""
        risk_factors = []
        
        # Low success rate indicates frustration
        if metrics.success_rate < 0.3:
            risk_factors.append(0.7)
        
        # Low engagement score
        if metrics.engagement_score < 0.3:
            risk_factors.append(0.8)
        
        # High death rate indicates difficulty issues
        death_rate = metrics.total_deaths / max(1, metrics.levels_completed)
        if death_rate > 3:
            risk_factors.append(0.6)
        
        # Low patience with multiple failures
        if metrics.patience_level < 0.3:
            risk_factors.append(0.5)
        
        return statistics.mean(risk_factors) if risk_factors else 0.2
    
    def _summarize_play_style(self, metrics: PlayerMetrics) -> Dict[str, str]:
        """Provide human-readable play style summary"""
        style = {}
        
        if metrics.aggressive_play_style > 0.7:
            style['combat'] = "Aggressive, prefers direct confrontation"
        elif metrics.aggressive_play_style < 0.3:
            style['combat'] = "Cautious, prefers strategic approach"
        else:
            style['combat'] = "Balanced combat style"
        
        if metrics.exploration_tendency > 0.7:
            style['exploration'] = "Explorer, enjoys discovering secrets"
        elif metrics.exploration_tendency < 0.3:
            style['exploration'] = "Goal-focused, prefers direct paths"
        else:
            style['exploration'] = "Moderate exploration"
        
        if metrics.patience_level > 0.7:
            style['persistence'] = "Patient, willing to retry challenges"
        elif metrics.patience_level < 0.3:
            style['persistence'] = "Prefers quick progression"
        else:
            style['persistence'] = "Moderate patience level"
        
        return style

class AdaptiveDifficultySystem:
    """Dynamic difficulty adjustment system"""
    
    def __init__(self, analytics: PlayerAnalytics):
        self.analytics = analytics
        self.difficulty_parameters = {
            'enemy_health_multiplier': 1.0,
            'enemy_damage_multiplier': 1.0,
            'enemy_count_multiplier': 1.0,
            'player_health_multiplier': 1.0,
            'resource_spawn_rate': 1.0,
            'checkpoint_frequency': 1.0
        }
        
        # Target performance metrics for optimal flow
        self.target_success_rate = 0.65
        self.target_engagement = 0.7
        self.adjustment_sensitivity = 0.1
    
    def adjust_difficulty(self, player_id: str, level_id: str) -> Dict[str, float]:
        """Calculate difficulty adjustments for a player and level"""
        
        analysis = self.analytics.get_player_analysis(player_id)
        if 'error' in analysis:
            return self.difficulty_parameters.copy()
        
        metrics = PlayerMetrics(**analysis['metrics'])
        adjustments = self.difficulty_parameters.copy()
        
        # Calculate adjustment magnitude based on performance deviation
        success_deviation = metrics.success_rate - self.target_success_rate
        engagement_deviation = metrics.engagement_score - self.target_engagement
        
        # Primary adjustment based on success rate
        if success_deviation > 0.2:  # Too easy
            multiplier = 1.0 + (success_deviation * self.adjustment_sensitivity)
            adjustments['enemy_health_multiplier'] *= multiplier
            adjustments['enemy_damage_multiplier'] *= min(1.3, multiplier)
            adjustments['enemy_count_multiplier'] *= multiplier
            adjustments['resource_spawn_rate'] *= max(0.7, 1.0 - success_deviation * 0.3)
            
        elif success_deviation < -0.2:  # Too hard
            multiplier = 1.0 + (abs(success_deviation) * self.adjustment_sensitivity)
            adjustments['enemy_health_multiplier'] /= multiplier
            adjustments['enemy_damage_multiplier'] /= multiplier
            adjustments['player_health_multiplier'] *= min(1.5, multiplier)
            adjustments['resource_spawn_rate'] *= multiplier
            adjustments['checkpoint_frequency'] *= multiplier
        
        # Secondary adjustments based on play style
        if metrics.aggressive_play_style > 0.7:
            # Aggressive players can handle more enemies
            adjustments['enemy_count_multiplier'] *= 1.1
        elif metrics.aggressive_play_style < 0.3:
            # Cautious players prefer fewer, stronger enemies
            adjustments['enemy_count_multiplier'] *= 0.9
            adjustments['enemy_health_multiplier'] *= 1.1
        
        if metrics.patience_level < 0.3:
            # Impatient players need more frequent checkpoints
            adjustments['checkpoint_frequency'] *= 1.3
        
        # Engagement-based adjustments
        if engagement_deviation < -0.2:
            # Low engagement - make game more forgiving
            adjustments['player_health_multiplier'] *= 1.2
            adjustments['resource_spawn_rate'] *= 1.2
        
        return adjustments
    
    def get_level_difficulty_recommendation(self, level_id: str) -> DifficultyLevel:
        """Recommend difficulty for a level based on global analytics"""
        level_stats = self.analytics.global_metrics['completion_rates_by_level'].get(level_id)
        
        if not level_stats or level_stats['attempts'] < 10:
            return DifficultyLevel.NORMAL
        
        completion_rate = level_stats['completions'] / level_stats['attempts']
        
        if completion_rate > 0.8:
            return DifficultyLevel.HARD
        elif completion_rate < 0.4:
            return DifficultyLevel.EASY
        else:
            return DifficultyLevel.NORMAL

# Example usage and simulation
def simulate_adaptive_difficulty():
    """Simulate adaptive difficulty system with player data"""
    
    analytics = PlayerAnalytics()
    difficulty_system = AdaptiveDifficultySystem(analytics)
    
    print("=== Player Analytics & Adaptive Difficulty Simulation ===")
    
    # Simulate player events
    players = ["player_1", "player_2", "player_3"]
    levels = ["level_1", "level_2", "level_3"]
    
    # Simulate different player types
    for i, player_id in enumerate(players):
        print(f"\\n--- Simulating {player_id} ---")
        
        # Different player skill levels
        skill_levels = [0.3, 0.6, 0.8]  # Beginner, Intermediate, Advanced
        skill = skill_levels[i]
        
        current_time = time.time()
        
        for level_id in levels:
            # Simulate level attempts
            attempts = 3 + int((1 - skill) * 5)  # More attempts for lower skill
            
            for attempt in range(attempts):
                # Level start
                analytics.track_event(GameEvent(
                    EventType.LEVEL_START, 
                    current_time, 
                    player_id, 
                    level_id, 
                    DifficultyLevel.NORMAL
                ))
                current_time += 1
                
                # Simulate gameplay events
                if skill > random.random():  # Success based on skill
                    # Successful completion
                    analytics.track_event(GameEvent(
                        EventType.LEVEL_COMPLETE, 
                        current_time + random.randint(30, 180), 
                        player_id, 
                        level_id, 
                        DifficultyLevel.NORMAL,
                        {'completion_time': random.randint(30, 180)}
                    ))
                    break
                else:
                    # Failed attempt
                    deaths = random.randint(1, 4)
                    for _ in range(deaths):
                        analytics.track_event(GameEvent(
                            EventType.PLAYER_DEATH, 
                            current_time + random.randint(10, 60), 
                            player_id, 
                            level_id, 
                            DifficultyLevel.NORMAL
                        ))
                    
                    analytics.track_event(GameEvent(
                        EventType.LEVEL_FAIL, 
                        current_time + random.randint(60, 300), 
                        player_id, 
                        level_id, 
                        DifficultyLevel.NORMAL
                    ))
                
                current_time += random.randint(60, 300)
        
        # Analyze player and get difficulty recommendations
        analysis = analytics.get_player_analysis(player_id)
        adjustments = difficulty_system.adjust_difficulty(player_id, "level_1")
        
        print(f"Player Analysis:")
        print(f"  Success Rate: {analysis['metrics']['success_rate']:.2f}")
        print(f"  Engagement Score: {analysis['metrics']['engagement_score']:.2f}")
        print(f"  Skill Progression: {analysis['metrics']['skill_progression']:.2f}")
        print(f"  Churn Risk: {analysis['churn_risk']:.2f}")
        print(f"  Recommended Difficulty: {analysis['recommended_difficulty']}")
        
        print(f"Difficulty Adjustments:")
        for param, value in adjustments.items():
            if value != 1.0:
                print(f"  {param}: {value:.2f}")

# Run simulation
simulate_adaptive_difficulty()
\`\`\`

## Real-Time Difficulty Adjustment

### Performance Monitoring
- Track player performance in real-time
- Identify frustration and boredom indicators
- Adjust difficulty mid-session when needed

### Flow State Optimization
- Maintain challenge-skill balance
- Prevent difficulty spikes that break immersion
- Provide appropriate feedback and rewards

### A/B Testing for Difficulty
- Test different difficulty curves
- Measure long-term engagement impact
- Optimize for player retention

Player analytics and adaptive difficulty systems create personalized gaming experiences that keep players engaged by automatically finding the optimal challenge level for each individual player.
  `,
  quiz: {
    id: 'analytics-difficulty-quiz',
    title: 'Player Analytics & Adaptive Difficulty Quiz',
    passThreshold: 75,
    questions: [
      {
        id: 'q1',
        question: 'What is the primary goal of adaptive difficulty systems?',
        type: 'multiple-choice',
        options: [
          'To make games easier for all players',
          'To maintain optimal challenge-skill balance for individual players',
          'To collect more player data',
          'To reduce development time'
        ],
        correctAnswer: 1,
        explanation: 'Adaptive difficulty aims to maintain the optimal balance between challenge and player skill to keep players in the "flow state" of engagement.',
        difficulty: 'medium',
        points: 20
      },
      {
        id: 'q2',
        question: 'Which metric is most important for identifying player churn risk?',
        type: 'multiple-choice',
        options: [
          'Total playtime',
          'Number of levels completed',
          'Success rate and engagement score combination',
          'Player age'
        ],
        correctAnswer: 2,
        explanation: 'Churn risk is best identified by combining multiple metrics, particularly success rate and engagement score, as they indicate both frustration and interest levels.',
        difficulty: 'medium',
        points: 15
      }
    ]
  }
};
